import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import DOMPurify from "dompurify";
import {
  ArrowLeft,
  BriefcaseBusiness,
  CheckCircle2,
  Clock3,
  CreditCard,
  FolderKanban,
  MessageSquareText,
  PackageCheck,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import StarRating from "../components/ui/StarRating";
import logger from "../utils/logger.js";
import "./DetailPages.css";
import {
  formatServiceCurrency,
  getFreelancerServiceDescription,
  getFreelancerServiceSummary,
  getFreelancerServiceTitle,
  getMyServiceOrders,
  getServiceDeliveryLabel,
  getServiceRevisionsLabel,
  getFreelancerServiceDetails,
  listServiceReviews,
} from "../api/freelancerServices";

const serviceFallbackImage =
  "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?auto=format&fit=crop&w=1400&q=80";

function getStatusMeta(status, isAr) {
  const map = {
    pending: { className: "text-bg-warning", label: isAr ? "قيد الانتظار" : "Pending" },
    paid: { className: "text-bg-primary", label: isAr ? "مدفوع" : "Paid" },
    in_progress: { className: "text-bg-info", label: isAr ? "قيد التنفيذ" : "In Progress" },
    delivered: { className: "text-bg-success", label: isAr ? "تم التسليم" : "Delivered" },
    completed: { className: "text-bg-success", label: isAr ? "مكتمل" : "Completed" },
    cancelled: { className: "text-bg-danger", label: isAr ? "ملغي" : "Cancelled" },
    disputed: { className: "text-bg-danger", label: isAr ? "نزاع" : "Disputed" },
  };

  return map[String(status || "").toLowerCase()] || {
    className: "text-bg-secondary",
    label: status || (isAr ? "غير معروف" : "Unknown"),
  };
}

function isBlockingOrder(order) {
  const status = String(order?.status || "").toLowerCase();
  const paymentStatus = String(order?.payment_status || order?.paymentStatus || "").toLowerCase();

  if (paymentStatus === "refunded") return false;
  return ["pending", "paid", "in_progress", "delivered", "disputed"].includes(status);
}

export default function FreelancerServiceDetails() {
  const { serviceRef } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { lang } = useLanguage();
  const { isLogged } = useAuth();
  const isAr = lang === "ar";

  const [service, setService] = useState(null);
  const [existingOrder, setExistingOrder] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  useEffect(() => {
    let active = true;

    async function load() {
      try {
        setLoading(true);
        setError("");
        setNotice("");

        const [details, serviceReviews] = await Promise.all([
          getFreelancerServiceDetails(serviceRef),
          listServiceReviews(serviceRef).catch(() => []),
        ]);
        if (!active) return;

        setService(details);
        setReviews(Array.isArray(serviceReviews) ? serviceReviews : []);

        if (isLogged) {
          try {
            const orders = await getMyServiceOrders();
            if (!active) return;
            const matched = orders.find(
              (item) => Number(item?.service?.id) === Number(details?.id) && isBlockingOrder(item),
            );
            setExistingOrder(matched || null);
          } catch (ordersError) {
            logger.warn("My service orders fetch failed", ordersError);
          }
        } else {
          setExistingOrder(null);
        }
      } catch (err) {
        logger.error("Freelancer service details error", err);
        if (!active) return;
        setError(isAr ? "تعذر تحميل تفاصيل الخدمة." : "Failed to load service details.");
      } finally {
        if (active) setLoading(false);
      }
    }

    load();
    return () => {
      active = false;
    };
  }, [serviceRef, isAr, isLogged]);

  const title = useMemo(() => getFreelancerServiceTitle(service, isAr), [service, isAr]);
  const summary = useMemo(() => getFreelancerServiceSummary(service, isAr), [service, isAr]);
  const description = useMemo(() => getFreelancerServiceDescription(service, isAr), [service, isAr]);
  const statusMeta = getStatusMeta(existingOrder?.status, isAr);
  const reviewAvg = Number(service?.rating || service?.average_rating || service?.stats?.reviews_avg_rating || 0);
  const reviewCount = Number(service?.reviews_count || service?.stats?.reviews_count || reviews.length || 0);
  const serviceUrlRef = service?.slug || service?.id || serviceRef;
  const coverImage = service?.cover_image || (service?.gallery_images && service.gallery_images.length ? service.gallery_images[0] : null) || service?.freelancer?.avatar_url || serviceFallbackImage;

  const facts = [
    {
      key: "freelancer",
      icon: UserRound,
      label: isAr ? "المستقل" : "Freelancer",
      value: service?.freelancer?.name || (isAr ? "مستقل" : "Freelancer"),
    },
    {
      key: "delivery",
      icon: Clock3,
      label: isAr ? "التسليم" : "Delivery",
      value: getServiceDeliveryLabel(service, isAr),
    },
    {
      key: "revisions",
      icon: RefreshCw,
      label: isAr ? "المراجعات" : "Revisions",
      value: getServiceRevisionsLabel(service, isAr),
    },
    {
      key: "orders",
      icon: PackageCheck,
      label: isAr ? "الطلبات" : "Orders",
      value: String(service?.orders_count || 0),
    },
    {
      key: "reviews",
      icon: MessageSquareText,
      label: isAr ? "التقييمات" : "Reviews",
      value: String(reviewCount),
    },
  ];

  function requireAuth() {
    navigate("/login", { state: { from: location } });
  }

  function handleCheckout() {
    if (!service) return;
    if (!isLogged) {
      requireAuth();
      return;
    }

    setActionLoading(true);
    navigate(`/freelancers/${serviceUrlRef}/checkout`);
  }

  const handleImageError = (event) => {
    if (event.currentTarget.dataset.fallbackApplied === "true") return;
    event.currentTarget.dataset.fallbackApplied = "true";
    event.currentTarget.src = serviceFallbackImage;
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-primary mb-3" role="status" />
        <p className="mb-0">{isAr ? "جاري تحميل الخدمة..." : "Loading service..."}</p>
      </div>
    );
  }

  if (error && !service) {
    return <Navigate to="/freelancers" replace />;
  }

  return (
    <div className="detail-pages-container course-detail-page freelancer-detail-page" dir={isAr ? "rtl" : "ltr"}>
      <div className="course-detail-shell">
        <div className="course-detail-backbar">
          <Link to="/freelancers" className="course-detail-backlink">
            <ArrowLeft size={16} />
            {isAr ? "الرجوع إلى الخدمات" : "Back to Services"}
          </Link>
        </div>

        <section className="course-detail-hero-v2">
          <div className="course-detail-hero-v2__main">
            <div className="detail-image-wrapper course-detail-image-wrap course-detail-hero-v2__media">
              <img src={coverImage} alt={title} className="detail-image course-detail-image" onError={handleImageError} />
            </div>

            <div className="course-detail-hero-v2__copy">
              <div className="course-detail-topline">
                <span className="course-detail-pill">
                  <FolderKanban size={14} />
                  {service?.category || (isAr ? "خدمة احترافية" : "Professional Service")}
                </span>
                <span className="course-detail-pill course-detail-pill--soft">
                  <Clock3 size={14} />
                  {getServiceDeliveryLabel(service, isAr)}
                </span>
              </div>

              <h1 className="detail-info-title course-detail-hero-v2__title">{title}</h1>

              <div className="course-detail-hero-v2__meta">
                <div className="detail-rating">
                  <StarRating value={reviewAvg || 0} size={18} showValue />
                  <span className="detail-reviews">({reviewCount} {isAr ? "تقييم" : "reviews"})</span>
                </div>
                <div className="course-detail-hero-v2__instructor">
                  <UserRound size={16} />
                  <span>{service?.freelancer?.name || (isAr ? "مستقل" : "Freelancer")}</span>
                </div>
              </div>

              {summary ? <p className="detail-desc course-detail-hero-v2__summary">{summary}</p> : null}

              <div className="course-detail-quick-stats">
                {facts.slice(1, 5).map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-quick-stat">
                      <span className="course-detail-quick-stat__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <aside className="course-detail-purchase-card">
            <div className="course-detail-purchase-card__eyebrow">
              <Sparkles size={14} />
              {isAr ? "طلب خدمة" : "Service order"}
            </div>

            <div className="detail-price course-detail-purchase-card__price">
              <span className="detail-current">
                {formatServiceCurrency(service?.price || 0, service?.currency || "USD")}
              </span>
            </div>

            {notice ? <div className="alert alert-success rounded-3 mb-3">{notice}</div> : null}
            {error ? <div className="alert alert-danger rounded-3 mb-3">{error}</div> : null}

            <div className="course-detail-purchase-card__actions">
              {existingOrder ? (
                <>
                  <div className="detail-modern-notice">
                    <span className={`badge ${statusMeta.className}`}>{statusMeta.label}</span>
                    <strong>{isAr ? "لديك طلب قائم لهذه الخدمة" : "You have an existing order"}</strong>
                  </div>
                  <Link to="/my-service-orders" className="btn btn-primary btn-lg course-detail-purchase-card__btn">
                    {isAr ? "عرض طلباتي" : "View My Orders"}
                  </Link>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary btn-lg course-detail-purchase-card__btn"
                  onClick={handleCheckout}
                  disabled={actionLoading}
                >
                  {actionLoading ? (isAr ? "جاري التحويل..." : "Redirecting...") : (isAr ? "اطلب الآن" : "Order Now")}
                </button>
              )}
            </div>

            <div className="course-detail-purchase-card__trust">
              <div>
                <ShieldCheck size={16} />
                <span>{isAr ? "تفاصيل الخدمة واضحة قبل الدفع" : "Clear service details before checkout"}</span>
              </div>
              <div>
                <CreditCard size={16} />
                <span>{isAr ? "انتقال آمن لصفحة الدفع" : "Secure transition to checkout"}</span>
              </div>
              <div>
                <CheckCircle2 size={16} />
                <span>{isAr ? "متابعة الطلب من صفحة طلباتي" : "Track progress from My Orders"}</span>
              </div>
            </div>
          </aside>
        </section>

        <section className="course-detail-layout-v2">
          <main className="course-detail-main-v2">
            <article className="course-detail-section-card">
              <div className="course-detail-section-card__head">
                <h3 className="detail-info-subtitle course-detail-section-card__title">
                  {isAr ? "وصف الخدمة" : "Service Description"}
                </h3>
              </div>
              <div
                className="detail-rich-text"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(description || (isAr ? "<p>لا يوجد وصف مفصل بعد.</p>" : "<p>No detailed description yet.</p>")),
                }}
              />
            </article>

            <div className="course-detail-dual-grid">
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h4 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "ماذا يشمل الطلب" : "What the order includes"}
                  </h4>
                </div>
                <div className="course-detail-side-notes">
                  <div>
                    <CheckCircle2 size={16} />
                    <span>{isAr ? "تسليم حسب مدة الخدمة المحددة." : "Delivery based on the service timeline."}</span>
                  </div>
                  <div>
                    <CheckCircle2 size={16} />
                    <span>{isAr ? "عدد مراجعات واضح قبل الدفع." : "Clear revision count before checkout."}</span>
                  </div>
                  <div>
                    <CheckCircle2 size={16} />
                    <span>{isAr ? "متابعة الطلب والتواصل من لوحة الطلبات." : "Order tracking and communication from the orders area."}</span>
                  </div>
                </div>
              </article>

              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h4 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "مناسب لمن" : "Best for"}
                  </h4>
                </div>
                <div className="course-detail-side-notes">
                  <div>
                    <BriefcaseBusiness size={16} />
                    <span>{isAr ? "أصحاب المشاريع الذين يحتاجون تنفيذًا واضحًا." : "Project owners who need clear execution."}</span>
                  </div>
                  <div>
                    <BriefcaseBusiness size={16} />
                    <span>{isAr ? "فرق العمل التي تبحث عن دعم متخصص." : "Teams looking for specialized support."}</span>
                  </div>
                  <div>
                    <BriefcaseBusiness size={16} />
                    <span>{isAr ? "من يريد خدمة جاهزة بدل توظيف طويل." : "Anyone who wants a ready service without a long hiring flow."}</span>
                  </div>
                </div>
              </article>
            </div>

            {reviews.length > 0 && (
              <article className="course-detail-section-card">
                <div className="course-detail-section-card__head">
                  <h3 className="detail-info-subtitle course-detail-section-card__title">
                    {isAr ? "التقييمات" : "Reviews"}
                  </h3>
                </div>
                <div className="detail-reviews-list course-detail-reviews-list">
                  {reviews.map((review) => (
                    <div key={review.id} className="detail-review-item course-detail-review-card">
                      <div className="d-flex justify-content-between mb-2 align-items-start gap-2">
                        <strong>{review.client?.name || review.user?.name || (isAr ? "عميل" : "Client")}</strong>
                        <StarRating value={review.rating || 0} size={14} showValue />
                      </div>
                      {review.title ? <div className="fw-semibold small mb-1">{review.title}</div> : null}
                      <p className="text-muted small mb-0">{review.comment}</p>
                    </div>
                  ))}
                </div>
              </article>
            )}
          </main>

          <aside className="course-detail-side-v2">
            <article className="course-detail-side-card">
              <h4>{isAr ? "معلومات الخدمة" : "Service Facts"}</h4>
              <div className="course-detail-facts">
                {facts.map((fact) => {
                  const Icon = fact.icon;
                  return (
                    <div key={fact.key} className="course-detail-fact">
                      <span className="course-detail-fact__icon">
                        <Icon size={16} />
                      </span>
                      <div>
                        <small>{fact.label}</small>
                        <strong>{fact.value}</strong>
                      </div>
                    </div>
                  );
                })}
              </div>
            </article>

            {existingOrder ? (
              <article className="course-detail-side-card course-detail-side-card--accent">
                <h4>{isAr ? "طلبك الحالي" : "Current Order"}</h4>
                <div className="course-detail-facts">
                  <div className="course-detail-fact">
                    <span className="course-detail-fact__icon"><PackageCheck size={16} /></span>
                    <div>
                      <small>{isAr ? "رقم الطلب" : "Order ID"}</small>
                      <strong>#{existingOrder.id}</strong>
                    </div>
                  </div>
                  <div className="course-detail-fact">
                    <span className="course-detail-fact__icon"><CheckCircle2 size={16} /></span>
                    <div>
                      <small>{isAr ? "الحالة" : "Status"}</small>
                      <strong>{statusMeta.label}</strong>
                    </div>
                  </div>
                </div>
              </article>
            ) : null}
          </aside>
        </section>
      </div>
    </div>
  );
}
